import type { OptionInterface } from '~/components/base/controls/use-input'
import type { CityInterface, CountryInterface } from '~/models/geo'
import { toAutocomplete } from '~/utils/autocomplete'

export interface GeoInterface {
  city: CityInterface
  country: CountryInterface
}

export const useGeo = () => {
  const baseURL = useRuntimeConfig().public.gatewayApi

  async function detectGeo() {
    const { data } = await useAuthFetch<{ data: Partial<GeoInterface> }>(
      '/geo/detect',
      {
        baseURL,
        method: 'POST',
      }
    )

    return {
      city: data?.city ? toAutocomplete(data.city) : null,
      country: data?.country ? toAutocomplete(data.country) : null,
    }
  }

  async function getCountriesAutocomplete(
    query?: string
  ): Promise<OptionInterface[]> {
    const { data } = await useAuthFetch<{ data: CountryInterface[] }>(
      '/geo/countries/autocomplete',
      {
        baseURL,
        params: {
          query,
        },
      }
    )

    return data.map(toAutocomplete)
  }

  async function getCountriesAll(): Promise<OptionInterface[]> {
    const { data } = await useAuthFetch<{ data: CountryInterface[] }>(
      '/geo/countries',
      {
        baseURL,
      }
    )

    return data.map(toAutocomplete)
  }

  async function getCountryById(id: string): Promise<OptionInterface> {
    const { data } = await useAuthFetch<{ data: CountryInterface }>(
      `/geo/countries/${id}`,
      {
        baseURL,
      }
    )

    return toAutocomplete(data)
  }

  async function getCitiesAutocomplete(
    country: string,
    query: string
  ): Promise<OptionInterface[]> {
    const { data } = await useAuthFetch<{ data: CityInterface[] }>(
      '/geo/cities/autocomplete',
      {
        baseURL,
        params: { country, query },
      }
    )

    return data.map(toAutocomplete)
  }

  async function getCityById(id: number): Promise<OptionInterface> {
    const { data } = await useAuthFetch<{ data: CityInterface }>(
      `/geo/cities/${id}`,
      {
        baseURL,
      }
    )

    return toAutocomplete(data)
  }

  return {
    detectGeo,
    getCountriesAutocomplete,
    getCountryById,
    getCitiesAutocomplete,
    getCityById,
    getCountriesAll,
  }
}
